"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ChromeLockContextValue = {
  locked: boolean;
  requestLock: () => void;
  releaseLock: () => void;
};

const ChromeLockContext = createContext<ChromeLockContextValue | null>(null);

function applyBodyChromeLock(locked: boolean) {
  document.documentElement.classList.toggle("cc-site--chrome-locked", locked);
  document.body.classList.toggle("cc-site--chrome-locked", locked);
}

export function CcSiteChromeLockProvider({ children }: { children: ReactNode }) {
  const [lockCount, setLockCount] = useState(0);
  const locked = lockCount > 0;

  const requestLock = useCallback(() => {
    setLockCount((count) => count + 1);
  }, []);

  const releaseLock = useCallback(() => {
    setLockCount((count) => Math.max(0, count - 1));
  }, []);

  useEffect(() => {
    applyBodyChromeLock(locked);
    return () => applyBodyChromeLock(false);
  }, [locked]);

  const value = useMemo(
    () => ({ locked, requestLock, releaseLock }),
    [locked, requestLock, releaseLock],
  );

  return (
    <ChromeLockContext.Provider value={value}>{children}</ChromeLockContext.Provider>
  );
}

export function useSiteChromeLocked(): boolean {
  return useContext(ChromeLockContext)?.locked ?? false;
}

/** Hide site chrome and lock document scroll while `active` is true. */
export function useSiteChromeLock(active: boolean): void {
  const context = useContext(ChromeLockContext);

  useEffect(() => {
    if (!context || !active) return;
    context.requestLock();
    return () => context.releaseLock();
  }, [active, context]);
}
